import React, { useEffect, useState } from 'react';
import { Container, Button, Alert, CircularProgress, Snackbar, Typography } from '@mui/material';
import { DealTable } from '../components/DealTable';
import { DealForm } from '../components/DealForm';
import { DealStageCards } from '../components/DealStageCards';
import { useDealStore } from '../store/dealStore';
import { getDeals, createDeal, updateDeal } from '../services/api';
import { Deal, CreateDealPayload } from '../types/deal';
import { useDealStagesStore } from '../store/dealStagesStore';
import { useOwnersStore } from '../store/ownersStore';
import { AuthStatus } from '../components/AuthStatus';
import { useAuth } from 'react-oidc-context';

export const Dashboard: React.FC = () => {
  const { deals, setDeals } = useDealStore();
  const { 
    currentPartnerName,
    loading: ownerLoading, 
    error: ownerError 
  } = useOwnersStore();
  const { dealStages, dealStagesList, loading: stagesLoading } = useDealStagesStore();
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [editDeal, setEditDeal] = useState<Deal | null>(null);
  const auth = useAuth();

  useEffect(() => {
    const fetchDeals = async () => {
      if (!currentPartnerName) return;
      
      try {
        setLoading(true);
        const response = await getDeals(currentPartnerName, auth.user?.access_token || '');
        setDeals(response.results);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDeals();
  }, [auth.user?.access_token, currentPartnerName, setDeals]);

  const filteredDeals = selectedStage 
    ? deals.filter(deal => deal.properties.dealstage === selectedStage)
    : deals;

  const handleCreateOrUpdateDeal = async (formData: CreateDealPayload) => {
    try {
      setLoading(true);
      
      if (editDeal) {
        // Update existing deal
        const response = await updateDeal(editDeal.id, formData, auth.user?.access_token || '');
        
        // Update the deals list
        setDeals(deals.map(deal => 
          deal.id === editDeal.id ? response : deal
        ));

        setNotification({
          open: true,
          message: 'Deal updated successfully!',
          severity: 'success'
        });
      } else {
        // Create new deal
        const response = await createDeal(formData, auth.user?.access_token || '');
        setDeals([response, ...deals]);

        setNotification({
          open: true,
          message: 'Deal created successfully!',
          severity: 'success'
        });
      }
      
      // Close the form and reset edit state
      setShowForm(false);
      setEditDeal(null);
    } catch (err: any) {
      setNotification({
        open: true,
        message: err.message || `Failed to ${editDeal ? 'update' : 'create'} deal`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (deal: Deal) => {
    setEditDeal(deal);
    setShowForm(true);
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  if (ownerLoading || loading || stagesLoading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (ownerError || error) {
    return (
      <Container>
        <Alert severity="error">
          {ownerError || error}
        </Alert>
      </Container>
    );
  }

  if (!currentPartnerName) {
    return (
      <Container>
        <Alert severity="error">
          Partner details not found. Please contact support.
        </Alert>
      </Container>
    );
  }

  return (
    <Container>

      <div className="header">
        <Typography component="div" className='header-title'>
          {currentPartnerName || 'Partner'} Sales Pipeline Dashboard
        </Typography>
        <Button
          className="button"
          variant="contained"
          color="primary"
          onClick={() => {
            setEditDeal(null); // Reset edit state when creating new deal
            setShowForm(true);
          }}
          sx={{ my: 2 }}
        >
          CREATE DEAL
        </Button>
        <AuthStatus />
      </div>
      

      {showForm && (
        <DealForm
          onSubmit={handleCreateOrUpdateDeal}
          onCancel={() => {
            setShowForm(false);
            setEditDeal(null);
          }}
          initialData={editDeal}
          accessToken={auth.user?.access_token}
        />
      )}

      <DealStageCards
        deals={deals}
        dealStages={dealStages}
        dealStagesList={dealStagesList}
        selectedStage={selectedStage}
        onStageSelect={setSelectedStage}
      />

      <DealTable
        deals={filteredDeals}
        onEdit={handleEdit}
        onDelete={(id) => {/* Implement delete logic */}}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}; 
